import React from 'react';

import BuildIcon from '@mui/icons-material/Build';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScienceIcon from '@mui/icons-material/Science';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowHorizontalFlow from './components/WorkflowHorizontalFlow';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowList from './components/WorkflowList';
import WorkflowOutputBox from './components/WorkflowOutputBox';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowSection from './components/WorkflowSection';
import WorkflowStepFlow from './components/WorkflowStepFlow';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const beSteps = [
  {
    label: 'Part1',
    title: 'スキーマ定義とAPI仕様書を作成する',
    path: '/devdocs/workflow/be-step1',
  },
  { label: 'Part2', title: 'API実装ワークフローとその全体像', path: '/devdocs/workflow/be-step2' },
  { label: 'Part3', title: 'Phase1: DD書の作成', path: '/devdocs/workflow/be-step3' },
  { label: 'Part4', title: 'Phase2: コード生成', path: '/devdocs/workflow/be-step4' },
  {
    label: 'Part5',
    title: 'Phase3: 単体テスト',
    path: '/devdocs/workflow/be-step5',
    active: true,
  },
  { label: 'Part6', title: 'E2Eテスト', path: '/devdocs/workflow/be-step6' },
  { label: '付録', title: '成果物サンプル集', path: '/devdocs/workflow/be-appendix' },
];

const WorkflowBeStep5: React.FC = () => {
  return (
    <WorkflowArticleLayout
      title="バックエンド開発編 Part5 — api_project_rule Phase3: 単体テスト"
      seriesName="バックエンド開発編"
      tags={['Backend', 'Test', 'Phase3']}
      seriesSteps={beSteps}
      prevLink={{ label: 'Part4: Phase2 コード生成', path: '/devdocs/workflow/be-step4' }}
      nextLink={{ label: 'Part6: E2Eテスト', path: '/devdocs/workflow/be-step6' }}
    >
      <WorkflowLearningPoints
        items={[
          'test.md（テスト仕様書）の作成方法と記載内容',
          'AIがローカルDocker環境でテストを自動実行する仕組み',
          'テスト失敗時の原因切り分けと設計書への遡及修正フロー',
        ]}
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: '概要' },
          { id: 'section-2', label: 'Phase3 の全体フロー' },
          { id: 'section-3', label: 'Step 3-1: test.md の作成' },
          { id: 'section-4', label: 'Step 3-2: ユーザー確認' },
          { id: 'section-5', label: 'Step 3-3: test.md の実行' },
          { id: 'section-6', label: 'Step 3-4: テスト完了' },
          { id: 'section-7', label: 'テスト失敗時の対応' },
          { id: 'section-8', label: '関連するステアリングファイル' },
          { id: 'section-9', label: '注意すべきこと' },
          { id: 'section-10', label: '成果物まとめ' },
          { id: 'section-11', label: 'ベストプラクティスとアンチパターン' },
          { id: 'section-12', label: 'api_project_rule ワークフロー完了' },
        ]}
      />

      {/* セクション1: 概要 */}
      <WorkflowSection title="概要" id="section-1" number={1}>
        <WorkflowText>
          Phase3はコード生成の品質を検証するフェーズです。API仕様書とDD書の設計情報をベースにテストケースを作成し、Docker環境でアプリケーションを起動して単体テストを実行します。
        </WorkflowText>

        <WorkflowTipBox title="なぜ本プロジェクトではJUnitを実施しないのか">
          本プロジェクトでは JUnit による単体テストを実施せず、curl
          ベースのAPI単体テストのみで品質を担保しています。これは「AI開発ではJUnitが不要」という主張ではなく、クレジット消費とのトレードオフによる判断です。
          {'\n\n'}
          一般的に JUnit
          のテストコードは実装コードの1.5〜3倍のコード量になります。AI駆動開発ではこのテストコード生成が大量のトークン出力
          = クレジット消費に直結します。
          {'\n\n'}
          ・JUnit を書く場合: 実装コードの2〜3倍のコード生成 →
          クレジット消費が大幅増加。モック設定のボイラープレートも多い
          {'\n'}
          ・curl テストに絞る場合: テスト仕様書（test.md）+ curl実行のみ →
          コード生成なしでクレジット消費を抑制
          {'\n\n'}
          その代わり、テストケースを「設計書ベース」で作成し、全バリデーション・全権限パターンを網羅することで、JUnit
          なしでも実装バグを検出できる体制にしています。
          {'\n\n'}
          JUnit
          が不要というわけではありません。プロジェクトの予算やチーム体制に余裕がある場合、またはビジネスロジックが複雑で境界値テストが多い場合は、JUnit
          の導入を推奨します。
        </WorkflowTipBox>

        <WorkflowHorizontalFlow
          steps={[
            {
              icon: <ScienceIcon sx={{ fontSize: 20 }} />,
              label: 'テストケース設計',
              sub: 'test.md 作成（正常系/異常系）',
            },
            {
              icon: <BuildIcon sx={{ fontSize: 20 }} />,
              label: 'テスト環境構築',
              sub: 'Docker起動 / DB初期化 / アプリ起動',
            },
            {
              icon: <PlayArrowIcon sx={{ fontSize: 20 }} />,
              label: 'テスト実行',
              sub: 'curl実行 / Response確認 / DB確認',
            },
          ]}
        />
      </WorkflowSection>

      {/* セクション2: Phase3 の全体フロー */}
      <WorkflowSection title="Phase3 の全体フロー" id="section-2" number={2}>
        <WorkflowStepFlow
          steps={[
            { label: '3-1. test.md の作成' },
            { label: '3-2. 【実行停止】ユーザー確認', highlight: true },
            { label: '3-3. test.md の実行 → test_result.md 出力' },
            { label: '3-4. 【実行停止】テスト完了・ユーザー確認', highlight: true },
          ]}
        />
      </WorkflowSection>

      {/* セクション3: Step 3-1 test.md の作成 */}
      <WorkflowSection title="Step 3-1: test.md の作成" id="section-3" number={3}>
        <WorkflowText>
          API仕様書の設計情報（実装ベースではなく）をもとに、当該APIの単体テストケースを作成します。
        </WorkflowText>

        <WorkflowSection title="テストケースの粒度" subNumber="3.1">
          <WorkflowText>
            本プロジェクトでは JUnit を実施していないため、通常 JUnit
            でカバーする観点（バリデーションの個別パターン、境界値、メソッド単位の入出力検証等）も含めた粒度でテストケースを作成しています。
          </WorkflowText>
          <WorkflowInfoBox type="info">
            JUnit を実施しているプロジェクトの場合: バリデーションの全パターン網羅や境界値テストは
            JUnit 側で担保できるため、curl
            ベースのテストケースは「APIとしての正常系・主要異常系・認証認可」に絞って構いません。テストケースの粒度はプロジェクトのテスト戦略に合わせて調整してください。
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowTipBox title="なぜ設計書ベースでテストケースを作るのか">
          AIにテストケースを作らせると、デフォルトでは「生成したコード（実装）を見て」テストを書こうとします。しかし実装ベースでテストを作ると、実装のバグがそのまま期待値に反映されてしまい、テストが必ず通る
          = バグを検出できない状態になります。
          {'\n\n'}
          本プロジェクトでは JUnit による単体テストを実施していないため、この curl
          ベースの単体テストが実装バグを検出する唯一の機会です。テストケースの期待値を「API仕様書の入出力仕様」から導出することで、実装とは独立した検証が可能になります。
          {'\n\n'}
          ・実装ベース: AIがコードを見て期待値を作る → コードのバグが期待値に含まれる → テスト通過 →
          バグ未検出
          {'\n'}
          ・設計書ベース: API仕様書の仕様から期待値を作る → コードが仕様と異なればテスト失敗 →
          バグ検出
          {'\n\n'}
          api_project_rule に「test.md
          は実装ベースではなく設計書ベースで入力と出力を生成すること」と明記しているのはこのためです。
        </WorkflowTipBox>

        <WorkflowSection title="配置先" subNumber="3.2">
          <WorkflowTable
            headers={['項目', '内容']}
            rows={[
              ['配置先', '.kiro/specs/10_API/{API_ID}/{ver}/'],
              ['ファイル名', 'test.md'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="テストケースのスコープ" subNumber="3.3">
          <WorkflowTable
            headers={['対象', '説明']}
            rows={[
              ['当該API単体', '対象APIの正常系・異常系を個別に確認する'],
              ['前提データの投入', 'テストに必要な前提データは関連APIを呼び出して作成する'],
              [
                '機能横断シナリオは対象外',
                '複数API組み合わせの業務シナリオテストは Part6（E2Eテスト）で実施する',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="test.md の構造" subNumber="3.4">
          <WorkflowCodeBlock
            title="test.md の構造例"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# API{ID} テスト

## テストケース一覧

| No | ケース名 | 確認観点 | 概要 |
|----|----------|----------|------|
| 1  | 正常系_基本登録 | 必須項目のみ | ... |
| 2  | 正常系_全項目登録 | 全項目入力 | ... |
| 3  | 異常系_必須未入力 | バリデーション | ... |
| 4  | 異常系_認証エラー | 認証 | ... |
| 5  | 異常系_権限不足 | 認可 | ... |
| 6  | 異常系_存在しないID | ビジネスエラー | ... |

---

## ケース1: 正常系_基本登録

### 概要

必須項目のみでパートナーを登録し、レスポンスとDBを確認する。

### 前提データ

（API経由で投入するデータがあればcurlを記載）

### API呼び出し

curl -s -X POST http://localhost:8080/v1/partner/create \\
  -H "Content-Type: application/json" \\
  -H "X-Session-Id: {sessionId}" \\
  -d '{"partnerName":"テスト株式会社",...}'

### 期待値

#### Response

{"partnerId": "(UUID 64桁)"}

#### DB確認

SELECT partner_id, partner_name FROM partner WHERE partner_name = 'テスト株式会社';

| partner_id | partner_name |
|-----------|--------------|
| (UUID)    | テスト株式会社 |`}
          />
        </WorkflowSection>

        <WorkflowSection title="単体テストの網羅ルール" subNumber="3.5">
          <WorkflowTable
            headers={['カテゴリ', '網羅内容']}
            rows={[
              ['正常系', '必須項目のみ / 全項目入力 / 境界値'],
              [
                'バリデーション',
                '必須チェック、桁数チェック、フォーマットチェック、Enumバリデーション',
              ],
              ['認証', 'セッションなし → 401'],
              ['認可', '全許可ロール + 権限不足ロール → 403'],
              ['ビジネスエラー', '存在しないID → 404、重複 → 400 等'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="前提データ投入のルール" subNumber="3.6">
          <WorkflowTable
            headers={['ルール', '説明']}
            rows={[
              [
                'API経由でデータ投入',
                'データを登録APIが実装済みの場合は極力そちらで前提データを作る。APIがない場合はSQL直接投入も可',
              ],
              ['初期ユーザーを起点', 'hiroki.chigira（ADMIN）でログイン→必要なデータを追加'],
              ['各ステップに期待値', 'Response + DB状態（確認SQL + 期待値テーブル）'],
              ['curlは改行なし', '1行で記載する'],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4: Step 3-2 ユーザー確認 */}
      <WorkflowSection title="Step 3-2: ユーザー確認" id="section-4" number={4}>
        <WorkflowText>test.md の内容をユーザーが確認します。</WorkflowText>
        <WorkflowTable
          headers={['入力', '結果']}
          rows={[
            ['0: #local_test.md 問題なし', 'Step 3-3（テスト実行）に進む'],
            ['1:指摘あり', 'テストケースを修正'],
          ]}
        />

        <WorkflowSection title="プロンプト例" subNumber="4.1">
          <WorkflowText>
            テスト実行に進む場合は #local_test.md を添えて 0: を入力します：
          </WorkflowText>
          <WorkflowCodeBlock language="text" code="0: #local_test.md 問題なし、テスト実行して" />
        </WorkflowSection>

        <WorkflowTipBox title="なぜ local_test.md を手動で渡すのか">
          local_test.md は inclusion: manual に設定しています。これは意図的な設計です。
          {'\n\n'}
          もし fileMatch にすると、Step 3-1（test.md 作成）の時点でも local_test.md
          が自動で読み込まれてしまいます。Step 3-1
          ではAIが大量のDD書・ソースコード・API仕様書を読み込んでテストケースを設計するため、この時点でDocker環境の手順まで読み込むとコンテキストを無駄に消費し、テストケース作成の精度が下がります。
          {'\n\n'}
          local_test.md が必要なのはStep 3-3（テスト実行）の時だけです。0: のタイミングでユーザーが
          #local_test.md
          を明示的に渡すことで、必要な時にだけルールが読み込まれ、コンテキストの無駄遣いを防げます。
        </WorkflowTipBox>

        <WorkflowSection title="確認ポイント" subNumber="4.2">
          <WorkflowList
            items={[
              'テストケースの網羅性（バリデーション全パターン含む）',
              '期待値の正確性（API仕様書と一致しているか）',
              '前提データの投入手順が正しいか',
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5: Step 3-3 test.md の実行 */}
      <WorkflowSection title="Step 3-3: test.md の実行" id="section-5" number={5}>
        <WorkflowText>
          test.md のテストケースを順番に実行し、結果を test_result.md
          に記録します。テスト環境の構築からテスト実行、後片付けまで全てAIが自動で行います。
        </WorkflowText>

        <WorkflowSection title="テスト実行ルール: local_test.md の紹介" subNumber="5.1">
          <WorkflowText>
            テスト環境の構築・実行手順を定義したSteeringファイルです。Front Matterは inclusion:
            manual を指定し、api_project_rule のPhase3実行時にAIが参照します。
          </WorkflowText>
          <WorkflowCodeBlock
            title="local_test.md 全文を見る"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# localでテストを行うためのルール

## docker

### docker-compose.yml 構成

| サービス | イメージ | コンテナ名 | ホストポート | 用途 |
|----------|----------|-----------|------------|------|
| mysql | mysql:8.0 | kanzhong-backend | 13306 | ローカル開発用MySQL |
| flyway | flyway/flyway:7.15.0 | flyway-migrate | - | DBマイグレーション実行 |

### テスト実行前のアプリケーション再起動

コード生成後にテストを実行する場合、既にアプリケーションが起動していると
旧コードで動作している可能性がある。テスト実行前に必ず以下の手順で再起動する。

1. 既存プロセスを停止する
2. テスト用DB（kanzhong_test）をクリーン＆マイグレーションする
3. テスト用DBに接続する環境変数を指定してアプリケーションを起動する

pkill -f "bootRun" 2>/dev/null
pkill -f "GradleDaemon" 2>/dev/null
sleep 2
docker compose run --rm flyway -url=jdbc:mysql://mysql:3306/kanzhong_test \\
  -user=root -password=root clean migrate
DB_URL="jdbc:mysql://localhost:13306/kanzhong_test?..." \\
  ./gradlew bootRun > /tmp/bootrun.log 2>&1 &
sleep 20

### テスト実行後のアプリケーション停止

pkill -f "bootRun" 2>/dev/null
pkill -f "GradleDaemon" 2>/dev/null`}
          />
        </WorkflowSection>

        <WorkflowSection title="結果の記録" subNumber="5.2">
          <WorkflowText>
            テスト実行結果は test_result.md として同じディレクトリに出力されます。
          </WorkflowText>
          <WorkflowCodeBlock
            title="test_result.md の構造例"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# API{ID} テスト結果

## 実施日時

2025-XX-XX HH:mm:ss

## 結果サマリ

| No | ケース名 | 結果 | 備考 |
|----|----------|------|------|
| 1  | 正常系_基本登録 | OK | |
| 2  | 異常系_必須未入力 | OK | |
| 3  | 異常系_認証エラー | NG | レスポンスコードが500 |

---

## ケース1: 正常系_基本登録

### 結果: OK

#### 実行curl

(実行したcurl)

#### 実際のResponse

{ "partnerId": "abc123..." }

#### DB確認結果

(SQL実行結果)

#### 判定: OK`}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6: Step 3-4 テスト完了 */}
      <WorkflowSection title="Step 3-4: テスト完了" id="section-6" number={6}>
        <WorkflowText>テスト完了後、AIは停止します。</WorkflowText>
        <WorkflowTable
          headers={['結果', '対応']}
          rows={[
            ['全ケースOK', 'api_project_rule ワークフロー完了'],
            ['NGケースあり', 'バグを修正して再テスト'],
          ]}
        />
      </WorkflowSection>

      {/* セクション7: テスト失敗時の対応 */}
      <WorkflowSection title="テスト失敗時の対応" id="section-7" number={7}>
        <WorkflowSection title="なぜバグ検知時のルールをあえて定義していないのか" subNumber="7.1">
          <WorkflowText>
            api_project_rule にはテスト失敗時の詳細な対応手順を意図的に定義していません。
          </WorkflowText>
          <WorkflowText>
            バグの原因はケースごとに異なります。バリデーション漏れ、SQL
            の条件ミス、型変換の不備、テストケース自体の期待値誤り——パターンは無数にあり、あらかじめ全てのパターンに対応するルールを書くことは現実的ではありません。
          </WorkflowText>
          <WorkflowText>
            ルールを定義しないことで、AIはテスト失敗時に自分で原因を調査し、実装やテストケースを修正して再実行するという柔軟な対応を取ります。固定的な手順に縛られず、ケースに応じた最適な修正を行えるのがメリットです。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="人間がやるべきこと: 設計書への遡及修正" subNumber="7.2">
          <WorkflowInfoBox type="important">
            AIはコードを直すが、設計書は直しません。
            {'\n\n'}
            テスト失敗時、AIは自力でコードを修正して試験をリトライします。しかし設計書（DD書・API仕様書・proposal等）の修正は行いません。
            {'\n\n'}
            test_result.md を確認してバグが検知されていた場合、以下を確認してください:
            {'\n\n'}
            1. AIがどのような実装修正を行ったかを確認する
            {'\n'}
            2. その修正が上流の設計書と乖離していないか判断する
            {'\n'}
            3. 乖離がある場合は根本原因のある設計書まで遡って修正するよう指示する
            {'\n\n'}
            遡及先は問題の性質に応じて変わります:
            {'\n'}
            ・コードとDD書の乖離 → DD書を修正
            {'\n'}
            ・DD書とAPI仕様書の乖離（例: I/Fでは任意だがDBでは必須）→ API仕様書を修正
            {'\n'}
            ・API仕様書と proposal の乖離 → proposal まで遡って修正
            {'\n\n'}
            要件定義書、設計書の整合性はこのワークフローの品質基盤です。コードだけ直して設計書が古いままになると、次回の開発時にAIが古い設計書を読んで誤ったコードを生成する原因になります。
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="対応フローの全体像" subNumber="7.3">
          <WorkflowText>
            テストNG時の対応は「AIの自動対応」と「人間の確認」に分かれます：
          </WorkflowText>
          <WorkflowTable
            headers={['対応者', 'ケース', 'アクション']}
            rows={[
              ['AI（自動）', 'コードのバグ', 'コード修正 → 再テスト'],
              ['AI（自動）', 'テスト期待値の誤り', 'test.md 修正 → 再テスト'],
              ['人間（レビュー後）', 'DD書の処理が不正', 'DD書修正を指示 → コード修正 → 再テスト'],
              [
                '人間（レビュー後）',
                'API仕様書の不備',
                'API仕様書修正 → DD書修正 → コード修正 → 再テスト',
              ],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション8: 関連するステアリングファイル */}
      <WorkflowSection title="関連するステアリングファイル" id="section-8" number={8}>
        <WorkflowTable
          headers={['ファイル', 'Phase3での役割']}
          rows={[
            ['api_project_rule.md', 'テストの進行ルール'],
            ['local_test.md', 'Docker環境の構築手順、アプリ起動手順'],
            ['api_specs_rule.md', 'テスト期待値の根拠（Request/Response仕様）'],
          ]}
        />
      </WorkflowSection>

      {/* セクション9: 注意すべきこと */}
      <WorkflowSection title="注意すべきこと" id="section-9" number={9}>
        <WorkflowTable
          headers={['注意点', '説明']}
          rows={[
            [
              'テストケースは設計書ベース',
              '実装を見てテストを書くのではなく、API仕様書の入出力仕様からテストを設計する',
            ],
            [
              '全バリデーションを網羅',
              '新規APIは全てのバリデーションルール（必須、桁数、フォーマット、Enum）をテスト',
            ],
            [
              'DB直接投入は極力避ける',
              '前提データはAPI経由で作成する。登録APIが未実装の場合のみSQL直接投入可',
            ],
            ['アプリ再起動を忘れない', 'コード修正後は必ずアプリを再起動してからテスト実行'],
            ['テスト後はアプリ停止', 'テスト完了後は必ず pkill -f "bootRun" で停止'],
          ]}
        />
      </WorkflowSection>

      {/* セクション10: 成果物まとめ */}
      <WorkflowSection title="成果物まとめ" id="section-10" number={10}>
        <WorkflowOutputBox>
          <WorkflowTable
            headers={['成果物', '配置先', '利用先']}
            rows={[
              ['test.md', '.kiro/specs/10_API/{API_ID}/{ver}/', 'テスト仕様書（実行の根拠）'],
              ['test_result.md', '.kiro/specs/10_API/{API_ID}/{ver}/', 'テスト結果の記録'],
            ]}
          />
        </WorkflowOutputBox>
      </WorkflowSection>

      {/* セクション11: ベストプラクティスとアンチパターン */}
      <WorkflowSection title="ベストプラクティスとアンチパターン" id="section-11" number={11}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: 'テストケースの期待値はAPI仕様書から導出する',
              effect: '実装バグが期待値に含まれることを防ぎ、独立した検証が可能になる',
            },
            {
              practice: '前提データは極力API経由で投入する',
              effect: '実際の業務フローを再現でき、登録API自体の動作確認にもなる',
            },
            {
              practice: '全バリデーションルールを網羅する（新規API）',
              effect:
                'バリデーション漏れはセキュリティリスクにもなるため、初回で全パターンを確認しておく',
            },
            {
              practice: 'テスト確認時に #local_test.md を添えて渡す',
              effect:
                'テスト実行に必要なDocker手順が適切なタイミングでのみ読み込まれ、コンテキスト消費を抑えられる',
            },
            {
              practice: 'test_result.md のNG内容を確認し、設計書への遡及修正を判断する',
              effect:
                'コードだけ直して設計書を放置すると、次回開発時にAIが古い設計書を参照してバグを再生成する',
            },
            {
              practice: 'テスト実行前にアプリを再起動する',
              effect:
                '旧コードで動作するプロセスが残っていると、修正が反映されずテスト結果が信頼できない',
            },
          ]}
        />

        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: '実装コードを見てテストケースを作る',
              effect:
                '実装のバグが期待値に反映され、テストが必ず通過する（バグ未検出）。API仕様書の入出力仕様から期待値を導出する',
            },
            {
              practice: 'DB直接投入で全ての前提データを作る',
              effect:
                '登録APIのバグが検出されない。データ整合性もAPI経由の方が信頼できる。登録APIが実装済みなら極力API経由で投入する',
            },
            {
              practice: 'テスト失敗時にコードだけ修正して設計書を放置する',
              effect:
                'DD書とコードが乖離し、次回のコード生成で同じバグが再発する。修正内容に応じてDD書→API仕様書→proposalまで遡及修正する',
            },
            {
              practice: 'テストケースの網羅性を確認せずに実行する',
              effect:
                '権限パターンやビジネスエラーが漏れ、本番で初めて不具合が発覚する。Step 3-2 で全カテゴリの網羅を確認する',
            },
            {
              practice: 'テスト後にアプリを停止し忘れる',
              effect:
                'ポートが占有されたまま残り、次回のテストやローカル開発に支障が出る。テスト完了後は必ず pkill -f "bootRun" で停止する',
            },
            {
              practice: '修正APIのバリデーション試験を全省略する',
              effect:
                '修正箇所に関連するバリデーションが壊れていても検出できない。修正箇所に影響するバリデーションルールは最低限テストする',
            },
          ]}
        />
      </WorkflowSection>

      {/* セクション12: api_project_rule ワークフロー完了 */}
      <WorkflowSection title="api_project_rule ワークフロー完了" id="section-12" number={12}>
        <WorkflowText>Phase3が完了すると、api_project_rule ワークフローが完了です。</WorkflowText>
        <WorkflowTable
          headers={['Phase', '成果物', '内容']}
          rows={[
            ['Phase1', 'diff.md, instructions.md, DD書', '設計書の作成'],
            ['Phase2', 'mk_instructions.md, Javaコード', 'コード生成'],
            ['Phase3', 'test.md, test_result.md', '単体テスト実行・確認'],
          ]}
        />

        <WorkflowSection title="最終的に得られるもの" subNumber="12.1">
          <WorkflowList
            items={[
              'API仕様書（確定版）',
              'DD書（コードと整合済み）',
              'Javaソースコード（ビルド＆テスト通過済み）',
              'Flywayマイグレーション',
              'テスト結果記録',
              'API一覧・権限一覧（最新化済み）',
              '用語集（最新化済み）',
            ]}
          />
        </WorkflowSection>

        <WorkflowText>
          これで1つのAPIの開発サイクルが完了です。次のAPIを開発する場合は、再度 Part1（スキーマ定義
          + API仕様書）から開始するか、既にAPI仕様書が存在する場合は直接 api_project_rule を起動して
          Part2 から開始します。
        </WorkflowText>
        <WorkflowText>
          全APIの単体テストが完了した後は、Part6（E2Eテスト）で機能横断のシナリオテストを実施します。
        </WorkflowText>
      </WorkflowSection>
    </WorkflowArticleLayout>
  );
};

export default WorkflowBeStep5;
